import { useCallback, useEffect, useRef, useState } from 'react'

import { validateSelectionReport } from '@/api/processing/services'
import { SelectionDivergence } from '@/api/processing/types'
import { DeedType, EducationStage } from '@/types/processing'
import { tusUploadFile } from '@/utils/tusUpload'

export type SelectionConsistencyStatus =
  | 'idle'
  | 'uploading'
  | 'checking'
  | 'valid'
  | 'divergent'
  | 'error'

export interface UseSelectionConsistencyParams {
  file: File | null
  nuDeedType: DeedType | null
  nuEducationStage: EducationStage | null
  userId?: number
}

interface UploadRecord {
  file: File
  settled: boolean
  promise: Promise<string>
}

export interface UseSelectionConsistencyResult {
  status: SelectionConsistencyStatus
  divergences: SelectionDivergence[]
  uploadId: string | null
  uploadProgress: number
  canSubmit: boolean
  retry: () => void
  reset: () => void
}

/**
 * PSV-352 — confere o relatório de escolha assim que ele é anexado.
 *
 * O upload foi antecipado do `handleSubmit` para o momento do anexo: antes não
 * havia arquivo no servidor para conferir, o que tornava impossível bloquear o
 * botão. O `uploadId` é reaproveitado no submit e só um `File` novo o invalida,
 * então trocar etapa ou tipo de obra revalida sem reenviar o arquivo, que
 * pode ter centenas de MB.
 *
 * Vive fora do modal para ser testável sem montar `Modal`/`Form`/`UploadFile`
 * do `@pnld/components-web`.
 */
const useSelectionConsistency = ({
  file,
  nuDeedType,
  nuEducationStage,
  userId,
}: UseSelectionConsistencyParams): UseSelectionConsistencyResult => {
  const [status, setStatus] = useState<SelectionConsistencyStatus>('idle')
  const [divergences, setDivergences] = useState<SelectionDivergence[]>([])
  const [uploadId, setUploadId] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [attempt, setAttempt] = useState(0)

  // Guarda a *promessa* do upload, não só o id: trocar a etapa no meio do envio
  // re-roda o efeito, e sem isso o mesmo arquivo, que pode ter centenas de
  // MB, subiria duas vezes.
  // `settled` diz se ela já terminou — é o que permite reexibir "enviando"
  // quando o efeito re-roda com o upload ainda em voo.
  const inFlightRef = useRef<UploadRecord | null>(null)
  // Duas trocas rápidas de etapa não podem deixar a resposta antiga
  // sobrescrever a nova.
  const requestSeqRef = useRef(0)

  // Idempotente de propósito: o efeito roda a cada mudança de identidade do
  // `File`, e criar array novo toda vez dispararia render em cadeia.
  const goIdle = useCallback(() => {
    setUploadProgress(previous => (previous === 0 ? previous : 0))
    setDivergences(previous => (previous.length === 0 ? previous : []))
    setStatus(previous => (previous === 'idle' ? previous : 'idle'))
  }, [])

  const reset = useCallback(() => {
    requestSeqRef.current += 1
    inFlightRef.current = null
    setUploadId(previous => (previous === null ? previous : null))
    goIdle()
  }, [goIdle])

  const retry = useCallback(() => {
    setAttempt(previous => previous + 1)
  }, [])

  useEffect(() => {
    if (!file) {
      reset()
      return
    }

    // Falsy, e não só `null`: limpar o Select grava string vazia, e `Number('')`
    // é 0 — que não existe em `DeedType`/`EducationStage`, ambos começando em 1.
    if (!nuDeedType || !nuEducationStage || !userId) {
      goIdle()
      return
    }

    requestSeqRef.current += 1
    const seq = requestSeqRef.current
    let cancelled = false
    const isCurrent = () => !cancelled && requestSeqRef.current === seq

    const run = async () => {
      let uploadFailed = false

      try {
        if (inFlightRef.current?.file !== file) {
          setUploadProgress(0)
          // O id antigo aponta para o arquivo anterior: se este upload falhar,
          // `canSubmit` do ramo `error` liberaria o submit com o arquivo errado.
          setUploadId(null)

          const record = { file, settled: false } as UploadRecord
          record.promise = tusUploadFile(file, userId, percentage => {
            if (inFlightRef.current?.file === file) {
              setUploadProgress(percentage)
            }
          })
          // `then` com os dois handlers, e não `finally`: `finally` devolve uma
          // promessa derivada que rejeitaria sem ninguém escutando.
          record.promise.then(
            () => {
              record.settled = true
            },
            () => {
              record.settled = true
            }
          )

          inFlightRef.current = record
        }

        // Fora do `if` de propósito: limpar e reescolher a etapa durante o
        // envio re-roda o efeito sem criar upload novo, e sem isto a tela
        // ficaria em `idle` — sem barra e sem explicação — até o upload acabar.
        if (!inFlightRef.current.settled) {
          setStatus('uploading')
        }

        const currentUploadId = await inFlightRef.current.promise.catch(
          error => {
            uploadFailed = true
            throw error
          }
        )

        if (!isCurrent()) return

        setUploadId(currentUploadId)
        setStatus('checking')

        const result = await validateSelectionReport({
          selectionUploadId: currentUploadId,
          nuDeedType,
          nuEducationStage,
        })

        if (!isCurrent()) return

        setDivergences(result.divergences ?? [])
        setStatus(result.valid ? 'valid' : 'divergent')
      } catch {
        // Só a falha do upload invalida a promessa guardada. Se quem falhou foi
        // a conferência, o retry reaproveita o arquivo que já subiu.
        if (uploadFailed && inFlightRef.current?.file === file) {
          inFlightRef.current = null
        }

        if (!isCurrent()) return

        // Falha técnica não é "arquivo divergente": a tela diz coisas
        // diferentes nos dois casos.
        setDivergences([])
        setStatus('error')
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [file, nuDeedType, nuEducationStage, userId, attempt, reset, goIdle])

  return {
    status,
    divergences,
    uploadId,
    uploadProgress,
    // `error` libera de propósito: a conferência é conveniência de UI, e a
    // guarda que de fato impede um processamento divergente de nascer é o
    // `ProcessingCreateSerializer`, que roda a mesma regra e devolve 400. Se o
    // endpoint estiver fora, travar aqui tiraria uma capacidade que já existe.
    //
    // Com uma ressalva: se quem falhou foi o *upload*, não há `uploadId` e não
    // há o que enviar — aí o botão continua travado, senão ficaria habilitado
    // sem fazer nada ao ser clicado.
    canSubmit: status === 'valid' || (status === 'error' && uploadId !== null),
    retry,
    reset,
  }
}

export default useSelectionConsistency
